import React, { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import type { DxfImportPhase } from '../../features/dxfImport/dxfImportSlice';
import { isRecoverableImportInterruptMessage } from '../../utils/dxfApi';
import { continueDxfImportRecovery } from '../../utils/startDxfImport';
import { store } from '../../store';

const UPLOAD_ROTATING_MESSAGES: Record<Exclude<DxfImportPhase, 'error'>, string[]> = {
  uploading: [
    'Uploading your DXF to the server…',
    'Transferring the file — large drawings can take a few minutes…',
    'Hang tight while the upload completes…',
  ],
  fetching: [
    'Processing drawing on the server…',
    'Extracting layers from the DXF…',
    'Loading floor plan geometry…',
    'Preparing polylines and layer data…',
  ],
  applying: [
    'Drawing layers onto the canvas…',
    'Fitting the floor plan to your view…',
  ],
};

const OPEN_ROTATING_MESSAGES: Record<Exclude<DxfImportPhase, 'error'>, string[]> = {
  uploading: ['Preparing to open your floor plan…'],
  fetching: [
    'Downloading floor plan geometry…',
    'Large drawings can take a few minutes to download…',
    'Parsing layers and polylines in the background…',
  ],
  applying: [
    'Drawing the floor plan onto the canvas…',
    'Fitting the view to your drawing…',
  ],
};

const UPLOAD_PHASE_TITLES: Record<Exclude<DxfImportPhase, 'error'>, string> = {
  uploading: 'Uploading DXF',
  fetching: 'Processing DXF',
  applying: 'Building floor plan',
};

const OPEN_PHASE_TITLES: Record<Exclude<DxfImportPhase, 'error'>, string> = {
  uploading: 'Opening floor plan',
  fetching: 'Loading floor plan',
  applying: 'Building floor plan',
};

/** Full-workspace blur overlay with animated import progress. */
const DxfImportOverlay: React.FC = () => {
  const dispatch = useAppDispatch();
  const dxfImport = useAppSelector((state) => state.dxfImport);
  const [tick, setTick] = useState(0);
  const [messageKey, setMessageKey] = useState(0);
  const [hideProgressAfterComplete, setHideProgressAfterComplete] = useState(false);

  const isOpenMode = dxfImport.mode === 'open';
  const rotatingMessages = isOpenMode ? OPEN_ROTATING_MESSAGES : UPLOAD_ROTATING_MESSAGES;
  const phaseTitles = isOpenMode ? OPEN_PHASE_TITLES : UPLOAD_PHASE_TITLES;

  const importing = dxfImport.active && dxfImport.phase !== 'error';
  const hasError = dxfImport.active && dxfImport.phase === 'error';
  const recoverableInterrupt =
    hasError &&
    !!dxfImport.error &&
    isRecoverableImportInterruptMessage(dxfImport.error);
  const visible = importing || hasError;

  const progressPercent = Math.min(100, Math.max(0, Math.round(dxfImport.progress ?? 0)));

  useEffect(() => {
    if (!importing) {
      setTick(0);
      setHideProgressAfterComplete(false);
    }
  }, [importing]);

  useEffect(() => {
    if (!importing) return;
    const id = window.setInterval(() => {
      setTick((n) => n + 1);
      setMessageKey((n) => n + 1);
    }, 2600);
    return () => window.clearInterval(id);
  }, [importing, dxfImport.generation]);

  useEffect(() => {
    setMessageKey((n) => n + 1);
  }, [dxfImport.phase, dxfImport.message]);

  useEffect(() => {
    if (!importing) {
      setHideProgressAfterComplete(false);
      return;
    }
    if (progressPercent >= 100) {
      const timer = window.setTimeout(() => {
        setHideProgressAfterComplete(true);
      }, 1000);
      return () => window.clearTimeout(timer);
    } else {
      setHideProgressAfterComplete(false);
    }
  }, [importing, progressPercent, dxfImport.generation]);

  const showProgress = progressPercent > 0 && !hideProgressAfterComplete;

  const displayMessage = useMemo(() => {
    if (hasError) {
      if (recoverableInterrupt) {
        return 'The upload connection dropped, but the server may still be finishing. Your DXF was likely accepted — keep waiting to open it, or return home and open it from the project list.';
      }
      return dxfImport.error || dxfImport.message || 'DXF import failed.';
    }
    if (!importing || dxfImport.phase === 'error') return '';

    if (dxfImport.message) {
      return dxfImport.message.replace(/\s*\(\d+%\)|\s*\d+%/g, '').trim();
    }
    const pool = rotatingMessages[dxfImport.phase] ?? rotatingMessages.fetching;
    return pool[tick % pool.length];
  }, [
    hasError,
    recoverableInterrupt,
    importing,
    rotatingMessages,
    dxfImport.error,
    dxfImport.message,
    dxfImport.phase,
    tick,
  ]);

  if (!visible) return null;

  const title =
    hasError
      ? recoverableInterrupt
        ? 'Still processing on server'
        : isOpenMode
          ? 'Could not open floor plan'
          : 'Import failed'
      : phaseTitles[dxfImport.phase as Exclude<DxfImportPhase, 'error'>] ??
        (isOpenMode ? 'Opening floor plan' : 'Importing DXF');

  return (
    <div
      className="dxf-import-overlay absolute inset-0 z-[80] flex items-center justify-center px-4"
      role="status"
      aria-live="polite"
      aria-busy={importing}
    >
      <div className="dxf-import-backdrop absolute inset-0" />
      <div className="dxf-import-orb dxf-import-orb-a" aria-hidden />
      <div className="dxf-import-orb dxf-import-orb-b" aria-hidden />

      <div className="dxf-import-card relative w-full max-w-[400px] overflow-hidden rounded-[24px] border border-white/80 bg-white/92 px-8 py-10 shadow-[0_30px_90px_rgba(15,23,42,0.22)] backdrop-blur-xl">
        <div className="pointer-events-none absolute inset-x-10 top-0 h-px bg-gradient-to-r from-transparent via-blue-300/80 to-transparent" />

        <div className="flex flex-col items-center text-center">
          {!hasError ? (
            <div className="dxf-import-hero relative mb-7 flex h-[112px] w-[112px] items-center justify-center" aria-hidden>
              <span className="dxf-import-pulse-ring dxf-import-pulse-ring-a" />
              <span className="dxf-import-pulse-ring dxf-import-pulse-ring-b" />
              <span className="dxf-import-pulse-ring dxf-import-pulse-ring-c" />

              <div className="dxf-import-blueprint relative h-[88px] w-[88px] overflow-hidden rounded-[22px]">
                <svg className="absolute inset-0 h-full w-full" viewBox="0 0 88 88" fill="none">
                  <rect width="88" height="88" rx="22" fill="url(#dxfBpFill)" />
                  <path
                    d="M18 28h22v14H18V28zm30 0h22v8H48V28zM18 50h14v20H18V50zm22 6h30v14H40V56z"
                    stroke="#93c5fd"
                    strokeWidth="1.5"
                    fill="rgba(255,255,255,0.35)"
                  />
                  <path
                    d="M14 14h60M14 74h60M14 14v60M74 14v60"
                    stroke="rgba(147,197,253,0.45)"
                    strokeWidth="1"
                    strokeDasharray="3 4"
                  />
                  <defs>
                    <linearGradient id="dxfBpFill" x1="12" y1="8" x2="76" y2="80" gradientUnits="userSpaceOnUse">
                      <stop stopColor="#eff6ff" />
                      <stop offset="1" stopColor="#dbeafe" />
                    </linearGradient>
                  </defs>
                </svg>
                <span className="dxf-import-scan" />
                <span className="dxf-import-orbit absolute inset-0 rounded-[22px]" />
                <span className="absolute inset-0 flex items-center justify-center text-[13px] font-bold tracking-[0.18em] text-blue-700">
                  DXF
                </span>
              </div>
            </div>
          ) : (
            <div className="dxf-import-error-icon mb-6 flex h-[72px] w-[72px] items-center justify-center rounded-full bg-amber-50 text-amber-600 ring-8 ring-amber-50/70">
              <svg className="h-8 w-8" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path
                  d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          )}

          <h2 className="text-[20px] font-semibold tracking-tight text-slate-900">{title}</h2>

          {(dxfImport.projectName || dxfImport.fileName) && (
            <p className="mt-2.5 max-w-full truncate rounded-full bg-slate-100/90 px-3.5 py-1 text-[12px] font-medium text-slate-600">
              {dxfImport.projectName || dxfImport.fileName}
              {dxfImport.fileName && dxfImport.projectName !== dxfImport.fileName
                ? ` · ${dxfImport.fileName}`
                : ''}
            </p>
          )}

          <div
            key={messageKey}
            className="dxf-import-message mt-5 min-h-[44px] max-w-[22rem] text-[13.5px] leading-relaxed text-slate-600"
          >
            {displayMessage}
          </div>

          {!hasError && showProgress && (
            <div className="mt-6 w-full">
              <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100 ring-1 ring-slate-200/80">
                <div
                  className="scene3d-loader-bar h-full rounded-full bg-gradient-to-r from-sky-400 via-blue-500 to-indigo-600 transition-all duration-300 ease-out"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>

              <div className="mt-3.5 flex items-center justify-center text-[11px] text-slate-400">
                <div className="flex items-center gap-1.5 font-medium text-slate-600">
                  <span className="dxf-import-dot h-1.5 w-1.5 rounded-full bg-blue-500" />
                  <span className="tabular-nums">{progressPercent}%</span>
                </div>
              </div>
            </div>
          )}

          {hasError && (
            <div className="mt-7 flex w-full flex-col items-center gap-3">
              {recoverableInterrupt && (
                <button
                  type="button"
                  className="rounded-xl bg-blue-600 px-5 py-2.5 text-[12px] font-semibold text-white shadow-[0_10px_24px_rgba(37,99,235,0.28)] transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
                  onClick={() =>
                    continueDxfImportRecovery({
                      dispatch,
                      getState: store.getState,
                    })
                  }
                >
                  Keep waiting
                </button>
              )}
              <p className="max-w-[20rem] text-[11px] leading-relaxed text-amber-700">
                {recoverableInterrupt
                  ? 'Or use Home in the toolbar and open the project if it already appears in the list.'
                  : 'Use the home control in the toolbar to return and try again.'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DxfImportOverlay;
